import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { login } from "endpoints/auth/index.action";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setAdmin } from "slices/admin/adminSlice";
import { AppDispatch } from "slices/store";
import conciergoLogo from "../../assets/images/svg/logos/conciergo-logo.svg";

const Login = () => {
  const [passwordShow, setPasswordShow] = useState(false);
  const REMEMBER_KEY = "rememberedEmail";
  const savedEmail =
    typeof window !== "undefined"
      ? localStorage.getItem(REMEMBER_KEY) || ""
      : "";
  const [rememberMe, setRememberMe] = useState(!!savedEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Check if user is already logged in
  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        const token =
          sessionStorage.getItem("token") || localStorage.getItem("token");
        if (token && token.trim() !== "") {
          // User is already logged in, redirect to dashboard or intended destination
          const from =
            (location.state as any)?.from ||
            new URLSearchParams(window.location.search).get("redirect") ||
            "/dashboard";
          navigate(from, { replace: true });
        }
      } catch (error) {
        console.warn("Could not check existing auth:", error);
        // Continue with login page if storage access fails
      }
    };

    checkExistingAuth();
  }, [navigate, location]);

  // Get initial email from localStorage if exists
  const getInitialEmail = (): string => {
    try {
      const rememberedEmail = localStorage.getItem("rememberedEmail");
      if (rememberedEmail && rememberedEmail.trim() !== "") {
        setRememberMe(true);
        return rememberedEmail;
      }
    } catch (error) {
      console.warn("Could not access localStorage:", error);
    }
    return "";
  };

  // Helper function to safely store token
  const storeToken = (
    token: string,
    useLocalStorage: boolean = false
  ): boolean => {
    try {
      if (useLocalStorage) {
        localStorage.setItem("token", token);
        // Also store in sessionStorage for immediate use
        sessionStorage.setItem("token", token);
      } else {
        sessionStorage.setItem("token", token);
        // Clear localStorage token if not using remember me
        localStorage.removeItem("token");
      }
      return true;
    } catch (error: any) {
      console.error("Error storing token:", error);
      // Handle quota exceeded error
      if (error.name === "QuotaExceededError") {
        toast.error(
          "Storage quota exceeded. Please clear your browser storage.",
          {
            autoClose: 5000,
          }
        );
      } else if (error.name === "SecurityError") {
        toast.error(
          "Cannot store token due to browser security restrictions.",
          {
            autoClose: 5000,
          }
        );
      } else {
        toast.error("Failed to store authentication token.", {
          autoClose: 3000,
        });
      }
      return false;
    }
  };

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setIsSubmitting(false);

      // Handle null/undefined response
      if (!data) {
        toast.error("No response from server. Please try again.", {
          autoClose: 3000,
          position: "top-right",
        });
        return;
      }

      // Handle backend response structure: { status: 'success' | 'error', message: string, data: {...} }
      if (data?.status === "error") {
        const errorMessage =
          data?.message ||
          "Login failed. Please check your credentials and try again.";
        toast.error(errorMessage, {
          autoClose: 4000,
          position: "top-right",
        });
        return;
      }

      if (data?.status === "success" && data?.data) {
        const { accessToken, user } = data.data;

        // Validate access token
        if (
          !accessToken ||
          typeof accessToken !== "string" ||
          accessToken.trim() === ""
        ) {
          toast.error(
            "Invalid authentication token received. Please try again.",
            {
              autoClose: 3000,
              position: "top-right",
            }
          );
          return;
        }

        // Store the access token
        const tokenStored = storeToken(accessToken, rememberMe);
        if (!tokenStored) {
          // Token storage failed, but we can still proceed (token is in memory)
          toast.warning(
            "Login successful, but could not save session. You may need to log in again after closing the browser.",
            {
              autoClose: 5000,
              position: "top-right",
            }
          );
        }

        // Handle remember me - store email
        try {
          if (rememberMe && user?.email) {
            localStorage.setItem("rememberedEmail", user.email);
          } else {
            localStorage.removeItem("rememberedEmail");
          }
        } catch (error) {
          console.warn("Could not save remembered email:", error);
        }

        // Set admin details from login response
        if (user) {
          const userDetails = {
            id: user.id || "",
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            role: user.role || "",
            profileImage: user.profileImage || "",
          };
          console.log("Dispatching setAdmin with:", userDetails);
          dispatch(setAdmin(userDetails));
        }

        toast.success("Welcome back to Conciergo!", {
          autoClose: 2000,
          position: "top-right",
        });

        formik.resetForm();

        // Navigate to intended destination or dashboard
        const from =
          (location.state as any)?.from ||
          new URLSearchParams(window.location.search).get("redirect") ||
          "/access-requests";
        try {
          navigate(from, { replace: true });
        } catch (navError) {
          console.error("Navigation error:", navError);
          // Fallback navigation
          window.location.href = from.startsWith("/")
            ? from
            : "/access-requests";
        }
      } else {
        toast.error(
          "Unexpected response format from server. Please try again.",
          {
            autoClose: 3000,
            position: "top-right",
          }
        );
      }
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      console.error("Login error:", error);

      let errorMessage = "Error logging in. Please try again later.";
      let errorTitle = "Login Failed";

      // Handle different types of errors
      if (error?.message) {
        const message = error.message.toLowerCase();

        // Network errors
        if (
          message.includes("failed to fetch") ||
          message.includes("networkerror")
        ) {
          errorMessage =
            "Network error. Please check your internet connection and try again.";
          errorTitle = "Connection Error";
        } else if (message.includes("timeout")) {
          errorMessage = "Request timed out. Please try again.";
          errorTitle = "Timeout Error";
        } else if (message.includes("cors")) {
          errorMessage = "CORS error. Please contact support if this persists.";
          errorTitle = "Configuration Error";
        } else if (message.includes("404")) {
          errorMessage =
            "Login endpoint not found. Please verify the API URL is correct.";
          errorTitle = "Endpoint Not Found";
        } else if (
          message.includes("401") ||
          message.includes("unauthorized")
        ) {
          errorMessage =
            "Invalid email or password. Please check your credentials.";
          errorTitle = "Authentication Failed";
        } else if (message.includes("403") || message.includes("forbidden")) {
          errorMessage = "Access forbidden. Your account may be restricted.";
          errorTitle = "Access Denied";
        } else if (message.includes("429") || message.includes("rate limit")) {
          errorMessage =
            "Too many login attempts. Please wait a few minutes and try again.";
          errorTitle = "Rate Limit Exceeded";
        } else if (
          message.includes("500") ||
          message.includes("server error")
        ) {
          errorMessage = "Server error. Please try again in a few moments.";
          errorTitle = "Server Error";
        } else if (
          message.includes("503") ||
          message.includes("service unavailable")
        ) {
          errorMessage =
            "Service temporarily unavailable. Please try again later.";
          errorTitle = "Service Unavailable";
        } else {
          errorMessage = error.message;
        }
      }

      toast.error(`${errorTitle}: ${errorMessage}`, {
        autoClose: 5000,
        position: "top-right",
      });
    },
  });

  const formik = useFormik({
    initialValues: {
      email: getInitialEmail(),
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required")
        .max(255, "Email address is too long")
        .trim(),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .max(128, "Password is too long")
        .required("Password is required")
        .matches(/^[\S]+$/, "Password cannot contain whitespace"),
    }),
    onSubmit: async (values) => {
      loginMutation.mutate(values);
      // sessionStorage.setItem("token", values.email);
      // navigate("/dashboard");
    },
  });
  console.log("Remember Me:", rememberMe);
  useEffect(() => {
    if (rememberMe) {
      if (formik.values.email) {
        localStorage.setItem(REMEMBER_KEY, formik.values.email);
      }
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  }, [rememberMe, formik.values.email]);

  return (
    <React.Fragment>
      <div className="auth-page-content d-flex align-items-center min-vh-100">
        <Container>
          <Row className="justify-content-center">
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div className="d-flex align-items-center justify-content-center">
                  <img src={conciergoLogo} alt="Conciergo Logo" height="40" />
                  <p className="ms-2 fs-1 fw-bold text-black mb-0">Conciergo</p>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4 border-top border-0 border-4 border-primary">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Welcome Back !</h5>
                    <p className="text-muted">
                      Sign in to continue to Conciergo.
                    </p>
                    {/* Show redirect message if coming from protected route */}
                    {(location.state as any)?.from && (
                      <Alert color="info" className="mt-3 mb-0">
                        <small>Please log in to access that page.</small>
                      </Alert>
                    )}
                  </div>
                  <div className="p-2 mt-4">
                    <Form onSubmit={formik.handleSubmit}>
                      <div className="mb-3">
                        <Label htmlFor="email" className="form-label">
                          Email
                        </Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder="Enter email"
                          type="email"
                          value={formik.values.email}
                          onChange={(e) => {
                            // Trim whitespace on change
                            const trimmedValue = e.target.value.trimStart();
                            formik.setFieldValue("email", trimmedValue);
                          }}
                          onBlur={formik.handleBlur}
                          onKeyDown={(e) => {
                            // Allow Enter key to submit from email field
                            if (
                              e.key === "Enter" &&
                              !formik.errors.email &&
                              formik.values.email
                            ) {
                              e.preventDefault();
                              const passwordInput = document.querySelector(
                                'input[name="password"]'
                              ) as HTMLInputElement;
                              if (passwordInput) {
                                passwordInput.focus();
                              }
                            }
                          }}
                          autoComplete="email"
                          autoFocus={!formik.values.email}
                          invalid={
                            !!(formik.touched.email && formik.errors.email)
                          }
                        />
                        {formik.touched.email && formik.errors.email && (
                          <FormFeedback type="invalid">
                            {formik.errors.email}
                          </FormFeedback>
                        )}
                      </div>
                      <div className="mb-3">
                        {/* <div className="float-end">
                            <Link to="/forgot-password" className="text-muted">Forgot password?</Link>
                          </div> */}
                        <Label className="form-label" htmlFor="password-input">
                          Password
                        </Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            name="password"
                            value={formik.values.password}
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5"
                            placeholder="Enter Password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            onKeyDown={(e) => {
                              // Submit on Enter key from password field
                              if (
                                e.key === "Enter" &&
                                !formik.errors.email &&
                                !formik.errors.password &&
                                formik.values.email &&
                                formik.values.password
                              ) {
                                e.preventDefault();
                                if (!loginMutation.isPending && !isSubmitting) {
                                  formik.handleSubmit();
                                }
                              }
                            }}
                            autoComplete="current-password"
                            invalid={
                              !!(
                                formik.touched.password &&
                                formik.errors.password
                              )
                            }
                          />
                          {formik.touched.password &&
                            formik.errors.password && (
                              <FormFeedback type="invalid">
                                {formik.errors.password}
                              </FormFeedback>
                            )}
                          <button
                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                            type="button"
                            id="password-addon"
                            onClick={() => setPasswordShow(!passwordShow)}
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </button>
                        </div>
                      </div>
                      <div className="form-check" style={{ display: 'none' }}>
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          id="auth-remember-check"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <Label
                          className="form-check-label"
                          htmlFor="auth-remember-check"
                        >
                          Remember me
                        </Label>
                      </div>
                      <div className="mt-4">
                        <Button
                          color="primary"
                          className="btn btn-primary w-100"
                          type="submit"
                          disabled={
                            loginMutation.isPending ||
                            isSubmitting ||
                            !formik.values.email?.trim() ||
                            !formik.values.password ||
                            !!formik.errors.email ||
                            !!formik.errors.password ||
                            formik.isSubmitting
                          }
                        >
                          {(loginMutation.isPending || isSubmitting) && (
                            <Spinner size="sm" className="me-2">
                              {" "}
                              Loading...{" "}
                            </Spinner>
                          )}
                          {loginMutation.isPending || isSubmitting
                            ? "Signing In..."
                            : "Sign In"}
                        </Button>
                      </div>
                      {loginMutation.isError && (
                        <div className="mt-3">
                          <Alert color="danger" className="mb-0">
                            <small>
                              <strong>Login failed.</strong> Please check your
                              credentials and try again.
                            </small>
                          </Alert>
                        </div>
                      )}
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Login;
